import React, { useState, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import proofIcon from "../assets/icons/Green/APX_Delivered._80x80.png";

import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  FetchPODDetails,
  HttpError,
  NotFoundError,
  UnknownError,
  VerifyPOD,
} from "../functions/ApiCalls";
import { useNavigate, useParams } from "react-router";
import { useTranslation, Trans } from "react-i18next";
import { useLocation } from "react-router-dom";

const PODOnDemandModal = () => {
  const { trackingNumber, referenceNumber } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const trackingNumberForAPI = (location?.state?.orderNo ?? trackingNumber) ?? referenceNumber; // #6463 - use either the tracking # on the page (preloaded) or the one in the URL address (direct-linking)

  const [currentPage, setCurrentPage] = useState(1);
  const [verificationError, setVerificationError] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [orderImages, setOrderImages] = useState([]); // State to hold the order images
  const [isLoading, setIsLoading] = useState(false);
  const [modalSize, setModalSize] = useState("md");// #6572 - state variable to control dialog size dynamically
  
  const downloadImagesAsZip = useCallback(() => {
    const zip = new JSZip();

    orderImages.forEach((imageData, index) => {
      // Convert base64 to a blob
      const byteCharacters = atob(imageData);
      const byteArrays = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArrays[i] = byteCharacters.charCodeAt(i);
      }

      // Add the blob to the zip
      zip.file(`image_${index + 1}.png`, byteArrays, { binary: true });
    });

    // Generate the zip file
    zip.generateAsync({ type: "blob" }).then((blob) => {
      // Save the zip file using file-saver
      saveAs(blob, "images.zip");
    });
  }, [orderImages]);

  const verifyPage1 = useCallback(
    async (event) => {
      event.preventDefault();

      const formData = new FormData(event.target);
      const accountNumber = formData.get("accountNumber").trim();
      const destinationPostalCode = formData
        .get("destinationPostalCode")
        .trim();
      
      if (accountNumber === "" && destinationPostalCode === "") {
        setVerificationError(t("PODOnDemandModal.verifyErrors.emptyValues"));
        return;
      }

      setIsLoading(true);

      const result = await VerifyPOD(
        trackingNumberForAPI, // #6399 - Use the order # from the state, rather than the value from the URL parameters (it could be a reference #)
        accountNumber,
        destinationPostalCode
      );

      //console.log ('trackingNumberForAPI: ' + trackingNumberForAPI, trackingNumber, location?.state?.orderNo, referenceNumber);

      if (result instanceof HttpError) {
        setVerificationError(t("PODOnDemandModal.verifyErrors.httpError"));
        setIsLoading(false);
        return;
      }

      if (result instanceof NotFoundError) {
        setVerificationError(t("PODOnDemandModal.verifyErrors.notFoundError"));
        setIsLoading(false);

        return;
      }

      if (result instanceof UnknownError) {
        setVerificationError(t("PODOnDemandModal.verifyErrors.unknownError"));
        setIsLoading(false);
        return;
      }

      const podDataResult = await FetchPODDetails(
        trackingNumberForAPI, // #6399 - Use the order # from the state, rather than the value from the URL parameters (it could be a reference #)
        accountNumber,
        destinationPostalCode
      );

      if (podDataResult instanceof HttpError) {
        setVerificationError(t("PODOnDemandModal.fetchErrors.httpError", { status: podDataResult.status }));
        setIsLoading(false);
        return;
      }

      if (podDataResult instanceof NotFoundError) {
        setVerificationError(t("PODOnDemandModal.fetchErrors.notFoundError"));
        setIsLoading(false);
        return;
      }

      if (podDataResult instanceof UnknownError) {
        setVerificationError(t("PODOnDemandModal.fetchErrors.unknownError"));
        setIsLoading(false);
        return;
      }

      setVerificationError(""); // Reset the verification error on success
      setIsLoading(false);
      setReceivedBy(podDataResult.receivedBy);
      setOrderImages(podDataResult.orderImage);
      setCurrentPage(currentPage + 1);
      setModalSize ("xl");// #6572 - Expand the dialog to show the POD images
    },
    [currentPage, t, trackingNumberForAPI]
  );

  const handleOnHide = () => {
    navigate("..", { relative: "path" });
  };

  return (
    <Modal
      show={true}
      onHide={handleOnHide}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size={modalSize}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <img src={proofIcon} alt="sms" className="me-2" height="40px"></img>
          <Trans i18nKey="PODOnDemandModal.title">
            Proof of Delivery & Signature
          </Trans>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentPage === 1 && (
          <form onSubmit={verifyPage1} style={{maxWidth: 'unset'}}>
              <fieldset
                  disabled={isLoading}
                  className="d-flex justify-content-center flex-column"
              >
                  <p style={{width: '94%'}}>
                      <Trans i18nKey="PODOnDemandModal.prompt">
                          Enter your account number{" "}
                          <b>
                              <u>or</u>
                          </b>{" "}
                          postal code so we can verify you.
                      </Trans>
                  </p>
                  <div className="mb-3">
                      <label htmlFor="accountNumber" className="form-label">
                          <Trans i18nKey="Generic.accountNumber">
                              Account Number
                          </Trans>
                      </label>
                      <input
                          type="text"
                          className="form-control"
                          id="accountNumber"
                          name="accountNumber"
                          aria-describedby="accountNumber"
                          placeholder="123456"
                          autoFocus={true}
                      />
                  </div>
                  
                  <div style={{position: 'relative', left: '25%'}}>
                      <div className="d-flex align-items-center" style={{width: '50%'}}>
                          <hr className="flex-grow-1" style={{width: '25%'}}/>
                          <span className="mx-2 text-muted" style={{fontWeight: '900'}}>OR</span>
                          <hr className="flex-grow-1" style={{width: '25%'}}/>
                      </div>
                  </div>
                  
                  <div className="mb-3">
                      <label htmlFor="destinationPostalCode" className="form-label">
                          <Trans i18nKey="Generic.destinationPostalCode">
                              Destination Postal Code
                          </Trans>
                      </label>
                      <input
                          type="text"
                          className="form-control"
                          id="destinationPostalCode"
                          name="destinationPostalCode"
                          aria-describedby="destinationPostalCode"
                          placeholder="L4W 5H8"
                          maxLength="7"
                      />
                  </div>

                  {verificationError && (
                      <b>
                          <p className="text-danger text-center">{verificationError}</p>
                      </b>
                  )}

                  <button className="btn btn-primary" type="submit">
                      {isLoading ? "..." : t("Generic.continue")}
                  </button>
              </fieldset>
          </form>
        )}

          {currentPage === 2 && (
              <div className="d-flex justify-content-center flex-column">
                  <Trans i18nKey="PODOnDemandModal.title">
                      Proof of Delivery & Signature
                  </Trans>
                  {orderImages.length > 0 ? (
                      <>
                          <div className="row justify-content-center">
                              {orderImages.map((imageData, index) => (
                            <div className="col-lg-4">
                                <div className="thumbnail">
                                    <img
                                        style={{
                                            objectFit: "scale-down",
                                        }}
                                        className="d-block w-100"
                                        src={`data:image/png;base64,${imageData}`}
                                        alt={`${index + 1}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                   
                    <button
                        className="btn btn-link mt-3"
                        onClick={downloadImagesAsZip}
                    >
                        <Trans i18nKey="PODOnDemandModal.downloadImages">
                            Download Images
                        </Trans>
                    </button>
                </>
            ) : (
                <p>
                    <Trans i18nKey="PODOnDemandModal.noImages">
                        There is no POD image for this order.
                    </Trans>
                </p>
            )}
              <br/>
              <p className="text-center">
                  <Trans i18nKey="PODOnDemandModal.receivedBy">
                      Received by: <b>{{receivedBy}}</b>
                  </Trans>
              </p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PODOnDemandModal;
